import type { Polygon } from 'geojson'
import { openRing, ringAreaM2 } from '@/shared/domain/zoneEditor'

/**
 * Zone de déneigement, section 18.2 du document 08.
 *
 * Le registre des types est celui du schéma legacy : il est fermé, et le
 * serveur refuse toute valeur hors liste. Aucun type n'est ajouté ici sans
 * migration correspondante.
 */
export const contractZoneTypes = [
  'entree',
  'stationnement',
  'trottoir',
  'escaliers',
  'aire_manoeuvre',
  'terrasse',
  'autre',
] as const

export type ContractZoneType = (typeof contractZoneTypes)[number]

export const contractZoneTypeLabel: Record<ContractZoneType, string> = {
  entree: 'Entrée',
  stationnement: 'Stationnement',
  trottoir: 'Trottoir',
  escaliers: 'Escaliers',
  aire_manoeuvre: 'Aire de manœuvre',
  terrasse: 'Terrasse',
  autre: 'Autre',
}

/**
 * Rôle d'une zone dans la mesure : une SURFACE est comptée, une EXCLUSION (îlot, bâtiment) est retirée
 * de l'union des surfaces une seule fois. Une exclusion n'est pas une consigne « zone interdite ».
 */
export type ContractZoneRole = 'SURFACE' | 'EXCLUSION'

export const contractZoneRoleLabel: Record<ContractZoneRole, string> = {
  SURFACE: 'Surface à déneiger',
  EXCLUSION: 'Exclusion (retirée de la surface)',
}

export interface ContractZone {
  id: string
  label: string
  type: ContractZoneType
  role: ContractZoneRole
  geometry: Polygon
  /** Surface calculée par le serveur; celle du navigateur n'est qu'indicative. */
  surfaceM2: number
  /** Zone GPS dérivée, section 18.3; absente si la dérivation a échoué. */
  gpsGeometry?: Polygon
  gpsBufferMeters?: number
  /** Capture de la vue au moment de la mesure, section 18.1; absente si elle a échoué. */
  imagePath?: string
  order: number
  capturedAt: string
}

export interface ContractZoneDraft {
  label: string
  type: ContractZoneType
  role: ContractZoneRole
  geometry: Polygon
  /** Chemin de la capture de vue dans le bucket privé, section 18.1. */
  imagePath?: string
}

export interface ReplaceContractZonesCommand {
  idempotencyKey: string
  contractId: string
  /** Révision lue avec les zones : une mesure modifiée entre-temps est refusée, jamais écrasée. */
  expectedRevision: number
  zones: ContractZoneDraft[]
  /** Marge de tolérance GPS, en mètres. Le serveur la borne à 50. */
  gpsBufferMeters?: number
}

export interface ReplaceContractZonesResult {
  contractId: string
  zones: number
  /** Surface nette enregistrée (union des surfaces moins les exclusions). */
  totalSurfaceM2: number
  gpsBufferMeters: number
  revision: number
}

/** Surface nette enregistrée et révision courante de la mesure d'un Contrat. */
export interface ContractMeasurementState {
  revision: number
  sumSurfaceM2: number
  overlapM2: number
  excludedM2: number
  netSurfaceM2: number
}

/** La mesure a changé depuis sa lecture (autre personne, autre onglet) : rien n'a été remplacé. */
export const CONTRACT_ZONES_CONFLICT = 'CONTRACT_ZONES_CONFLICT'

/**
 * Lecture des zones refusée parce qu'une ligne est illisible. L'éditeur se bloque alors :
 * réenregistrer une mesure partielle effacerait les zones manquantes.
 */
export const CONTRACT_ZONES_READ_INCOMPLETE = 'CONTRACT_ZONES_READ_INCOMPLETE'

/** Marge par défaut : deux mètres absorbent l'imprécision d'un GPS de camion. */
export const defaultGpsBufferMeters = 2

/**
 * Surface indicative affichée pendant le tracé, avant que le serveur ne la
 * recalcule. Formule de l'aire sphérique, suffisante pour un aperçu.
 */
export function approximateAreaM2(geometry: Polygon): number {
  const ring = geometry.coordinates[0]
  if (!ring || ring.length < 4) return 0
  return Math.round(ringAreaM2(openRing(ring)) * 100) / 100
}

export function formatSurface(surfaceM2: number): string {
  return `${new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 1 }).format(surfaceM2)} m²`
}
